/* eslint-disable import/prefer-default-export */
import { put, call, select } from 'redux-saga/effects';
import _assign from 'lodash/assign';
import { setSaveInProgress } from '../actions/test';
import {
  videoUploadApi,
  saveOrUpdateProblemSolution,
  audioUploadApi,
} from '../api/test';
import { captureException } from '../utils/sentry';
import ActionTypes from '../constants/ActionTypes';
import { showErrorToast, showSuccessToast } from '../actions/toast';
import { NETWORK_ERROR, SAVE_IN_PROGRESS } from '../constants/Http';
import { getApiErrorData } from '../utils/helperFunction';
import { ctaClick, getBandwidthData } from '../utils/trackingUtil';
import { runCodeWithTestCases } from '../api/coding';
import { problemLabel, problemTypes } from '../constants/ProblemTypes';
import { videoSolution } from '../components/ProblemTypes/Video/constants';
import {
  audioSolution,
  EAudioRecordingStatus,
} from '../components/ProblemTypes/Audio/constants';
import { SESSION_STATE } from '../constants/AssessmentConstants';
import { progressStart } from '../actions';
import { MESSAGE_ON_SAVE_AND_CLOSE } from '../components/ProblemTypes/PodBasedSolution/constants';
import { UBA_EVENT_NAMES } from '../constants/Constants';

export const createSolnObj = subProblemList => {
  const subSolnObj = {};
  subProblemList.map(subProblem => {
    if (subProblem.solution) {
      subSolnObj[subProblem.slug] = subProblem.solution;
    }
    return null;
  });
  return subSolnObj;
};

/*
  Todo:
    1. Need to fix perameter for all problem types, so we can use this for function for every problem type
    2. Currently we are using this only for MCQ and Subjective.
 */
/**
 * @param {object} args
 * @param {string|undefined} args.problemSlug
 * @param {string|undefined} args.slug
 * @param {object} args.solution
 * prepare solution and save
 */

export function* redirectAfterTestSubmit() {
  try {
    const {
      isUploadInProgress,
      isAudioUploadInProgress,
      isTestSubmitting,
    } = yield select(state => state.test);
    const isVideoUploading = Object.values(isUploadInProgress).filter(
      inProgress => inProgress === true
    );
    const isAudioUploading = Object.values(isAudioUploadInProgress).filter(
      inProgress => inProgress === EAudioRecordingStatus.UPLOADING
    );
    if (
      (isVideoUploading.length > 0 || isAudioUploading.length > 0) &&
      isTestSubmitting
    ) {
      return;
    }
    yield put(progressStart());
    yield put({
      type: ActionTypes.SET_TEST_SUBMITTING,
      payload: false,
    });
    const {
      slug,
      redirection_url: redirectionUrl,
      test_type: testType,
      enable_feedback_page: enableFeedbackPage,
    } = yield select(state => state.test);
    const resp = yield call(getFeedbackApi, slug);
    if (resp.status === 200) {
      if (redirectionUrl) window.location.replace(redirectionUrl);
      else if (testType === 'LRN' && enableFeedbackPage) {
        window.location.replace(`/test-report/${slug}`);
      } else {
        window.location.replace(`/test/${slug}/feedback`);
      }
    }
  } catch {
    const { slug } = yield select(state => state.test);
    window.location.replace(`/test/${slug}/feedback`);
  }
}

function* videoUpload(videoPutUrl, videoBlob, problemSlug, progressBar) {
  const solutions = yield select(state => state.solutions);
  const { totalRetries = 0 } = solutions[problemSlug] || {};
  const { uploadSpeed, downloadSpeed } = getBandwidthData();
  const fileSizeInKB = (videoBlob?.size || 0) / 1024;

  const ubaPayload = {
    label: `retryCount: ${totalRetries}`,
    source: problemSlug,
    triggeredFrom: 'video',
    fileSize: fileSizeInKB,
    bandwidth: `Upload: ${uploadSpeed} Mbps, download: ${downloadSpeed} Mbps`,
  };

  try {
    yield put({
      type: `${ActionTypes.VIDEO_UPLOAD}_INCREMENT_RETRIES`,
      totalRetries: totalRetries + 1,
      problemSlug,
    });
    yield put({
      type: ActionTypes.SET_VIDEO_UPLOAD_STATUS,
      isUploadInProgress: true,
      problemSlug,
    });
    yield call(videoUploadApi, videoPutUrl, videoBlob, progressBar);

    yield call(ctaClick, {
      eventName: UBA_EVENT_NAMES.problemClick,
      payload: {
        ...ubaPayload,
        cta: 'upload success',
      },
    });

    yield put({
      type: ActionTypes.SET_VIDEO_UPLOAD_STATUS,
      isUploadInProgress: false,
      problemSlug,
    });
    yield put({
      type: ActionTypes.SET_VIDEO_UPLOAD_FAILED,
      isVideoUploadFailed: false,
      problemSlug,
    });
    yield put({
      type: `${ActionTypes.VIDEO_UPLOAD}_STATUS`,
      status: true,
      problemSlug,
    });
    const { isTestSubmitting } = yield select(state => state.test);
    if (isTestSubmitting) yield call(redirectAfterTestSubmit);
  } catch (err) {
    if (totalRetries < 2) {
      yield call(videoUpload, videoPutUrl, videoBlob, problemSlug, progressBar);
    } else {
      const videoUploadError = new Error(videoSolution.VIDEO_UPLOAD_ERROR, {
        cause: err,
      });

      yield call(ctaClick, {
        eventName: UBA_EVENT_NAMES.problemClick,
        payload: { ...ubaPayload, cta: 'upload fail' },
      });

      yield put({
        type: ActionTypes.SET_VIDEO_UPLOAD_STATUS,
        isUploadInProgress: false,
        problemSlug,
      });
      yield put({
        type: ActionTypes.SET_VIDEO_UPLOAD_FAILED,
        isVideoUploadFailed: true,
        problemSlug,
      });
      const { isTestSubmitting } = yield select(state => state.test);
      if (isTestSubmitting) yield call(redirectAfterTestSubmit);
      throw videoUploadError;
    }
  }
}

function* audioUpload(audioPutUrl, audioBlob, problemSlug) {
  const { solutions, indexedProblems } = yield select(state => {
    return {
      solutions: state.solutions,
      indexedProblems: state.problems.indexedProblems,
    };
  });
  const { totalRetries = 0 } = solutions[problemSlug] || {};
  try {
    yield put({
      type: `${ActionTypes.AUDIO_UPLOAD}_INCREMENT_RETRIES`,
      totalRetries: totalRetries + 1,
      problemSlug,
    });
    yield call(audioUploadApi, audioPutUrl, audioBlob);
    yield put(
      showSuccessToast(
        `<a href="#${problemLabel.ADO}_${problemSlug}_section">Question ${indexedProblems[problemSlug]}</a> audio uploaded successfully`
      )
    );
    yield put({
      type: ActionTypes.SET_AUDIO_UPLOAD_STATUS,
      isAudioUploadInProgress: EAudioRecordingStatus.UPLOADED,
      problemSlug,
    });
    yield put({
      type: `${ActionTypes.AUDIO_UPLOAD}_STATUS`,
      status: true,
      problemSlug,
    });
    const { isTestSubmitting } = yield select(state => state.test);
    if (isTestSubmitting) yield call(redirectAfterTestSubmit);
  } catch (err) {
    if (totalRetries < 5) {
      yield call(audioUpload, audioPutUrl, audioBlob, problemSlug);
    } else {
      const audioUploadError = new Error(audioSolution.AUDIO_UPLOAD_ERROR, {
        cause: err,
      });
      yield put({
        type: ActionTypes.SET_AUDIO_UPLOAD_STATUS,
        isAudioUploadInProgress: EAudioRecordingStatus.FAILED,
        problemSlug,
      });
      const { isTestSubmitting } = yield select(state => state.test);
      if (isTestSubmitting) yield call(redirectAfterTestSubmit);
      throw audioUploadError;
    }
  }
}

export function* prepareSolutionAndSave(args) {
  let resp;
  try {
    const { saveInProgress } = yield select(state => state.appStatus);
    const { clusterSlug } = args?.solution || {};
    if (saveInProgress && !saveInProgress[args.problemSlug]) {
      const { auth, testSolutionSet, test } = yield select(state => ({
        auth: state.auth.initUserDetails,
        testSolutionSet: state.testSolutionSet,
        test: state.test,
      }));
      yield put(setSaveInProgress(args.problemSlug, true));
      const solutionObj = {
        creator: `/api/v1/user/${auth.username}`,
        test_solution_set: `api/v1/testsolutionset/${testSolutionSet.id}`,
        test: `/api/v1/test/${test.slug}`,
      };

      const newSolution = _assign({}, solutionObj, args.solution);
      resp = yield call(
        saveOrUpdateProblemSolution,
        auth.user_id,
        auth.usertype,
        test.slug,
        args.problemSlug,
        newSolution,
        args.solutionSlug,
        undefined,
        args.retryCallback
      );

      if (
        args.solution.solution_type === problemTypes.TEX &&
        !args.isAutoSave
      ) {
        yield put(showSuccessToast('Answer updated successfully!'));
      }

      if (args.solution.solution_type === problemTypes.VID) {
        const videoPutUrl = resp.data.video_post_url;
        yield videoUpload(
          videoPutUrl,
          args.solution.blob,
          args.problemSlug,
          args.solution.videoUploadProgressBar
        );
      }

      if (args.solution.solution_type === problemTypes.ADO) {
        const audioPutUrl = resp.data.audio_post_url;
        yield audioUpload(audioPutUrl, args.solution.blob, args.problemSlug);
      }
      if (clusterSlug) {
        yield put({
          type: ActionTypes.SET_CLUSTER_ATTEMPTS_COUNT,
          problem: clusterSlug,
        });
        yield put({
          type: `${ActionTypes.SAVE_CLUSTER_SOLUTION}_SUCCESS`,
          payload: {
            clusterSlug,
            ...(resp.data || {}),
          },
        });
      } else {
        yield put({
          type: ActionTypes.SET_ATTEMPTS_COUNT,
          problem: args.problemSlug,
        });
        yield put({
          type: `${ActionTypes.SAVE_SOLUTION}_SUCCESS`,
          payload: resp.data,
        });
      }
      return resp;
    }
    const error = new Error(SAVE_IN_PROGRESS);
    captureException(error);
    throw error;
  } catch (error) {
    const { toast, isTestSubmitted } = yield select(state => ({
      toast: state.appStatus.toast,
      isTestSubmitted: state.test.isTestSubmitted,
      slug: state.test.slug,
    }));
    const errorInfo = getApiErrorData(error, 'UPS');
    if (
      args.solution.solution_type === problemTypes.VID &&
      error.message === videoSolution.VIDEO_UPLOAD_ERROR
    ) {
      const VideoErrorInfo = getApiErrorData(error.cause, 'VID');

      if (VideoErrorInfo && VideoErrorInfo.message) {
        yield put(
          showErrorToast(VideoErrorInfo.message, VideoErrorInfo.apiErrorInfo)
        );
      }
      yield put({
        type: `${ActionTypes.VIDEO_UPLOAD}_STATUS`,
        status: false,
        problemSlug: args.problemSlug,
      });
      yield put({
        type: ActionTypes.SET_VIDEO_UPLOAD_FAILED,
        isVideoUploadFailed: true,
        problemSlug: args.problemSlug,
      });
      yield put({
        type: `${ActionTypes.VIDEO_UPLOAD}_RESET_RETRIES`,
        totalRetries: 0,
        problemSlug: args.problemSlug,
      });
      throw error;
    }

    if (args.solution.solution_type === problemTypes.ADO) {
      yield put({
        type: ActionTypes.SET_AUDIO_UPLOAD_STATUS,
        isAudioUploadInProgress: EAudioRecordingStatus.FAILED,
        problemSlug: args.problemSlug,
      });

      if (error.message === audioSolution.AUDIO_UPLOAD_ERROR) {
        const audioErrorInfo = getApiErrorData(error.cause, 'ADO');

        if (audioErrorInfo && audioErrorInfo.message) {
          yield put(
            showErrorToast(audioErrorInfo.message, audioErrorInfo.apiErrorInfo)
          );
        }
        yield put({
          type: `${ActionTypes.AUDIO_UPLOAD}_STATUS`,
          status: false,
          problemSlug: args.problemSlug,
        });
        yield put({
          type: `${ActionTypes.AUDIO_UPLOAD}_RESET_RETRIES`,
          totalRetries: 0,
          problemSlug: args.problemSlug,
        });
        throw error;
      }
    }

    if (
      errorInfo &&
      errorInfo.message &&
      !toast?.apiErrorInfo &&
      !args.isAutoSave &&
      !isTestSubmitted
    ) {
      yield put(showErrorToast(errorInfo.message, errorInfo.apiErrorInfo));
    }
    if (error.response?.data?.action?.redirection) {
      yield put(
        updateTestExpiryAndSessionState({
          is_expired: true,
          session_state: SESSION_STATE.END,
        })
      );
      yield call(redirectAfterTestSubmit);
    }
    if (error.message === NETWORK_ERROR) {
      // eslint-disable-next-line consistent-return
      return;
    }
    throw error;
  } finally {
    yield put(setSaveInProgress(args.problemSlug, false));
  }
}

export function* preparePlaygroundSolutionAndSave(args) {
  try {
    if (args.solution?.isSubmit) {
      yield put(progressStart(MESSAGE_ON_SAVE_AND_CLOSE));
    }
    const { saveInProgress } = yield select(state => state.appStatus);
    if (saveInProgress && !saveInProgress[args.problemSlug]) {
      const { auth, test } = yield select(state => ({
        auth: state.auth.initUserDetails,
        test: state.test,
      }));
      yield put(setSaveInProgress(args.problemSlug, true));
      const solutionObj = {
        creator: `/api/v1/user/${auth.username}`,
      };

      const newSolution = { ...solutionObj, ...args.solution };
      const resp = yield call(
        saveOrUpdateProblemSolution,
        auth.user_id,
        auth.usertype,
        test.slug,
        args.problemSlug,
        newSolution,
        args.slug,
        'playground'
      );

      yield put({
        type: `${ActionTypes.SAVE_SOLUTION}_SUCCESS`,
        payload: resp.data,
      });

      if (args.solution?.isSubmit) {
        yield call(runCodeWithTestCases, {
          solution_id: resp.data.id,
        });
      }
      if (args.solution?.isSubmit) {
        // check for window close
        window.close();
      }
      return resp;
    }
    const error = new Error(SAVE_IN_PROGRESS);
    captureException(error);
    throw error;
  } catch (error) {
    if (error.message === NETWORK_ERROR) {
      // eslint-disable-next-line consistent-return
      return;
    }
    throw error;
  } finally {
    yield put(setSaveInProgress(args.problemSlug, false));
  }
}
export function* solutionIdMapsToProblemSlug(action) {
  const solutions = yield select(state => state.solutions);

  const problemSlug = Object.keys(solutions).find(
    sol => solutions[sol].id === action.solution_id
  );
  if (problemSlug) {
    return {
      problemType: solutions[problemSlug].solution_type,
      problemSlug,
      solutionSlug: solutions[problemSlug].slug,
    };
  }
  return {
    problemType: null,
    problemSlug: null,
    solutionSlug: null,
  };
}

export function isRetryableNetworkError(error) {
  const msg = error?.message || '';
  const code = error?.code;

  return (
    !navigator.onLine ||
    code === 'ERR_NETWORK_CHANGED' ||
    msg.includes('ERR_NETWORK_CHANGED') ||
    code === 'ECONNABORTED' ||
    code === 'ENOTFOUND' ||
    msg.includes('Network Error') ||
    msg.includes('Failed to fetch')
  );
}
